import { gsap } from 'gsap';

$(document).ready(() => {
  const captureBtn = document.getElementById('captureBtn');
  const loadingMask = document.getElementById('loadingMask');

  if (!captureBtn || !loadingMask) return;

  const QR_SIZE = 108;
  const MOBILE_BREAKPOINT = 1081;
  const MOBILE_SCROLL_SELECTORS = ['#home_md', '#about_md'];
  const MOBILE_SECTION_SELECTORS = ['#home_md .md6', '#about_md .md6'];
  const PAGE_NETWORK_TIMEOUT_MS = 15000;
  const NETWORK_IDLE_MS = 400;

  function isMobileViewport() {
    return window.innerWidth < MOBILE_BREAKPOINT;
  }

  function waitForImageLoad(img) {
    if (img.complete) return Promise.resolve();

    return new Promise((resolve) => {
      img.addEventListener('load', resolve, { once: true });
      img.addEventListener('error', resolve, { once: true });
    });
  }

  function waitWithTimeout(promise, timeoutMs) {
    return Promise.race([
      promise,
      new Promise((resolve) => setTimeout(resolve, timeoutMs)),
    ]);
  }

  async function waitForPageNetworkReady() {
    if (document.readyState !== 'complete') {
      await waitWithTimeout(
        new Promise((resolve) => window.addEventListener('load', resolve, { once: true })),
        PAGE_NETWORK_TIMEOUT_MS
      );
    }

    document.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });

    const deadline = Date.now() + PAGE_NETWORK_TIMEOUT_MS;
    const remainingMs = () => Math.max(0, deadline - Date.now());

    await waitWithTimeout(
      Promise.all([...document.images].map(waitForImageLoad)),
      remainingMs()
    );

    if (document.fonts?.ready) {
      await waitWithTimeout(document.fonts.ready, remainingMs());
    }

    await waitWithTimeout(new Promise((resolve) => setTimeout(resolve, NETWORK_IDLE_MS)), remainingMs());
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
  }

  function getShareFooterContext() {
    const foot = document.querySelector(
      isMobileViewport() ? 'footer .md_foot' : 'footer .pc_foot'
    );
    if (!foot) return null;

    const infoCare = foot.querySelector('.info_care');
    const infoUs = foot.querySelector('.info_us');

    return {
      foot,
      infoCare,
      infoUs,
      careTitle: infoCare?.querySelector('.con_title'),
      qrcodeSection: foot.querySelector('.qrcode-section'),
      qrcodeContainer: foot.querySelector('.qr-box'),
      qrTip: foot.querySelector('.qr-tip'),
      footerSocialQrcodes: foot.querySelector('.footer-social-qrcodes'),
      isMobile: isMobileViewport(),
    };
  }

  function getPageScrollApi() {
    return window.homeScroll || window.aboutScroll || null;
  }

  function needsMobileScrollFix() {
    return MOBILE_SCROLL_SELECTORS.some((selector) => document.querySelector(selector));
  }

  function freezeAnimations() {
    const saved = [];

    document.querySelectorAll('.animate__animated').forEach((el) => {
      saved.push({
        el,
        animation: el.style.animation,
        opacity: el.style.opacity,
        transform: el.style.transform,
        visibility: el.style.visibility,
      });

      el.style.animation = 'none';
      el.style.opacity = '1';
      el.style.transform = 'none';
      el.style.visibility = 'visible';
    });

    return saved;
  }

  function restoreAnimations(saved) {
    saved.forEach(({ el, animation, opacity, transform, visibility }) => {
      el.style.animation = animation;
      el.style.opacity = opacity;
      el.style.transform = transform;
      el.style.visibility = visibility;
    });
  }

  async function prepareViewportForCapture(isMobile) {
    const state = {
      scrollY: window.scrollY,
      bodyOverflow: document.body.style.overflow,
      scrollContainers: [],
      mobileSections: [],
      animationStates: freezeAnimations(),
    };
    const pageScroll = getPageScrollApi();
    const wrapper = document.querySelector('.page-scroll-wrapper');

    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    if (isMobile && needsMobileScrollFix()) {
      MOBILE_SCROLL_SELECTORS.forEach((selector) => {
        const el = document.querySelector(selector);
        if (!el) return;

        state.scrollContainers.push({
          el,
          overflow: el.style.overflow,
          height: el.style.height,
          scrollTop: el.scrollTop,
        });

        el.scrollTop = 0;
        el.style.overflow = 'visible';
        el.style.height = 'auto';
      });

      MOBILE_SECTION_SELECTORS.forEach((selector) => {
        document.querySelectorAll(selector).forEach((el) => {
          state.mobileSections.push({
            el,
            height: el.style.height,
            overflow: el.style.overflow,
          });
          el.style.height = 'auto';
          el.style.overflow = 'visible';
        });
      });
    } else if (pageScroll && wrapper && !isMobile) {
      state.pageScroll = pageScroll;
      state.wrapper = wrapper;
      gsap.killTweensOf(wrapper);
      state.wrapperY = gsap.getProperty(wrapper, 'y');
      gsap.set(wrapper, { y: 0 });
      pageScroll.refresh?.();
      document.body.style.overflow = 'auto';
    }

    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    return state;
  }

  function restoreViewportAfterCapture(state) {
    restoreAnimations(state.animationStates || []);

    document.body.style.overflow = state.bodyOverflow || '';

    state.scrollContainers?.forEach(({ el, overflow, height, scrollTop }) => {
      el.style.overflow = overflow;
      el.style.height = height;
      el.scrollTop = scrollTop;
    });

    state.mobileSections?.forEach(({ el, overflow, height }) => {
      el.style.overflow = overflow;
      el.style.height = height;
    });

    if (state.pageScroll && state.wrapper) {
      if (state.wrapperY != null) {
        gsap.set(state.wrapper, { y: state.wrapperY });
      }
      state.pageScroll.refresh?.();
      return;
    }

    window.scrollTo(0, state.scrollY);
  }

  function buildShareQrcode(container, url) {
    container.innerHTML = '';
    new QRCode(container, {
      text: url,
      width: QR_SIZE,
      height: QR_SIZE,
      colorDark: '#222222',
      colorLight: '#ffffff',
      correctLevel: QRCode.CorrectLevel.M,
    });

    const qrCanvas = container.querySelector('canvas');
    if (!qrCanvas) return;

    const qrDataUrl = qrCanvas.toDataURL('image/png');
    container.innerHTML = '';
    const qrImg = document.createElement('img');
    qrImg.src = qrDataUrl;
    qrImg.alt = '页面分享二维码';
    qrImg.width = QR_SIZE;
    qrImg.height = QR_SIZE;
    container.appendChild(qrImg);
  }

  function activateShareQrcode(context, url) {
    const {
      qrcodeSection,
      qrcodeContainer,
      qrTip,
      footerSocialQrcodes,
      infoCare,
      infoUs,
      careTitle,
      isMobile,
    } = context;

    buildShareQrcode(qrcodeContainer, url);
    qrTip.textContent = '扫码查看页面';

    if (careTitle) {
      context.savedCareTitleDisplay = careTitle.style.display;
      careTitle.style.display = 'none';
    }

    if (isMobile) {
      context.savedInfoUsHeight = infoUs?.style.height || '';
      context.savedInfoUsOverflow = infoUs?.style.overflow || '';
      context.savedInfoCareMinHeight = infoCare?.style.minHeight || '';

      if (infoUs) {
        infoUs.style.height = 'auto';
        infoUs.style.overflow = 'visible';
      }
      if (infoCare) {
        infoCare.style.minHeight = '1.6rem';
      }
      if (footerSocialQrcodes) footerSocialQrcodes.style.display = 'none';

      qrcodeSection.classList.add('is-active');
      qrcodeSection.style.display = 'flex';
      return;
    }

    if (footerSocialQrcodes) footerSocialQrcodes.style.visibility = 'hidden';
    qrcodeSection.style.display = 'flex';
  }

  function deactivateShareQrcode(context) {
    const {
      qrcodeSection,
      qrcodeContainer,
      qrTip,
      footerSocialQrcodes,
      infoCare,
      infoUs,
      careTitle,
      isMobile,
    } = context;

    if (careTitle) {
      careTitle.style.display = context.savedCareTitleDisplay || '';
    }

    if (isMobile) {
      if (infoUs) {
        infoUs.style.height = context.savedInfoUsHeight || '';
        infoUs.style.overflow = context.savedInfoUsOverflow || '';
      }
      if (infoCare) {
        infoCare.style.minHeight = context.savedInfoCareMinHeight || '';
      }
      if (footerSocialQrcodes) footerSocialQrcodes.style.display = '';
    } else if (footerSocialQrcodes) {
      footerSocialQrcodes.style.visibility = '';
    }

    qrcodeSection.classList.remove('is-active');
    qrcodeSection.style.display = 'none';
    qrcodeContainer.innerHTML = '';
    qrTip.textContent = '';
  }

  async function capturePage(viewportState) {
    const baseOptions = {
      useCORS: true,
      allowTaint: false,
      backgroundColor: '#ffffff',
      scale: Math.min(window.devicePixelRatio || 2, 2),
      scrollX: 0,
      scrollY: 0,
    };

    const isSpecialPage = !!(
      viewportState?.pageScroll ||
      (viewportState?.scrollContainers && viewportState.scrollContainers.length)
    );

    if (isSpecialPage) {
      const captureTarget = document.documentElement;
      return html2canvas(captureTarget, {
        ...baseOptions,
        width: Math.max(captureTarget.scrollWidth, document.body.scrollWidth),
        height: Math.max(captureTarget.scrollHeight, document.body.scrollHeight),
        windowWidth: document.documentElement.clientWidth,
        windowHeight: document.documentElement.clientHeight,
      });
    }

    return html2canvas(document.documentElement, baseOptions);
  }

  function showLoadingMask() {
    loadingMask.style.display = 'flex';
    requestAnimationFrame(() => {
      loadingMask.classList.add('is-visible');
    });
  }

  function hideLoadingMask() {
    loadingMask.classList.remove('is-visible');
    setTimeout(() => {
      if (!loadingMask.classList.contains('is-visible')) {
        loadingMask.style.display = 'none';
      }
    }, 250);
  }

  function playShareBtnAnimation(className) {
    captureBtn.classList.remove('is-sharing', 'is-success');
    void captureBtn.offsetWidth;
    captureBtn.classList.add(className);
  }

  captureBtn.addEventListener('click', async () => {
    const shareFooter = getShareFooterContext();
    if (!shareFooter?.qrcodeSection || !shareFooter?.qrcodeContainer || !shareFooter?.qrTip) return;

    captureBtn.disabled = true;
    playShareBtnAnimation('is-sharing');

    await new Promise((resolve) => setTimeout(resolve, 280));
    showLoadingMask();

    let viewportState = null;
    let isSuccess = false;

    try {
      activateShareQrcode(shareFooter, window.location.href);
      viewportState = await prepareViewportForCapture(shareFooter.isMobile);
      await waitForPageNetworkReady();

      const canvas = await capturePage(viewportState);
      openImageInNewPage(canvas.toDataURL('image/png'));
      isSuccess = true;
    } catch (err) {
      console.error('截图失败:', err);
      alert('截图失败，请重试');
    } finally {
      hideLoadingMask();

      if (viewportState) restoreViewportAfterCapture(viewportState);
      deactivateShareQrcode(shareFooter);

      if (isSuccess) {
        playShareBtnAnimation('is-success');
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      captureBtn.classList.remove('is-sharing', 'is-success');
      captureBtn.disabled = false;
    }
  });

  // ── 浮动二维码按钮 ──
  const qrCodeBtn = document.getElementById('qrCodeBtn');
  const qrFloatPopup = document.getElementById('qrFloatPopup');
  const floatQrcode = document.getElementById('floatQrcode');

  function buildFloatQrcode(container, url) {
    container.innerHTML = '';
    new QRCode(container, {
      text: url,
      width: 128,
      height: 128,
      colorDark: '#222222',
      colorLight: '#ffffff',
      correctLevel: QRCode.CorrectLevel.M,
    });

    const qrCanvas = container.querySelector('canvas');
    if (!qrCanvas) return;

    const qrDataUrl = qrCanvas.toDataURL('image/png');
    container.innerHTML = '';
    const qrImg = document.createElement('img');
    qrImg.src = qrDataUrl;
    qrImg.alt = '页面二维码';
    qrImg.width = 128;
    qrImg.height = 128;
    container.appendChild(qrImg);
  }

  if (qrCodeBtn && qrFloatPopup && floatQrcode) {
    qrCodeBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      const isVisible = qrFloatPopup.classList.contains('is-visible');

      if (isVisible) {
        qrFloatPopup.classList.remove('is-visible');
      } else {
        buildFloatQrcode(floatQrcode, window.location.href);
        qrFloatPopup.classList.add('is-visible');
      }
    });

    document.addEventListener('click', function (e) {
      if (qrFloatPopup.classList.contains('is-visible') &&
          !qrFloatPopup.contains(e.target) &&
          e.target !== qrCodeBtn &&
          !qrCodeBtn.contains(e.target)) {
        qrFloatPopup.classList.remove('is-visible');
      }
    });
  }

  function openImageInNewPage(dataUrl) {
    var parts = dataUrl.split(',');
    var mimeMatch = parts[0].match(/:(.*?);/);
    var mimeType = mimeMatch ? mimeMatch[1] : 'image/png';
    var byteString = atob(parts[1]);
    var ab = new ArrayBuffer(byteString.length);
    var ia = new Uint8Array(ab);
    for (var i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    var blob = new Blob([ab], { type: mimeType });
    var url = URL.createObjectURL(blob);

    var win = window.open(url, '_blank');
    if (!win) {
      URL.revokeObjectURL(url);
      alert('弹窗被拦截，将在当前页面预览截图');
      showImageOverlay(dataUrl);
    }
  }

  function showImageOverlay(dataUrl) {
    var overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed; inset:0; background:#000; z-index:2000; display:flex; align-items:center; justify-content:center; flex-direction:column;';

    var img = document.createElement('img');
    img.src = dataUrl;
    img.style.cssText = 'max-width:100%; max-height:85vh; object-fit:contain;';

    var btn = document.createElement('button');
    btn.textContent = '关闭';
    btn.style.cssText = 'margin-top:16px; padding:10px 20px; border:none; border-radius:20px; background:#fff;';
    btn.onclick = function () { overlay.remove(); };

    overlay.appendChild(img);
    overlay.appendChild(btn);
    document.body.appendChild(overlay);
  }
});
